import type { BookDetail, DraftRead, WorkspaceDraft } from "@contract";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { api, errorCodeOf } from "../api/fetcher";
import { queryKeys } from "../api/keys";

type Snapshot = {
  value: object;
  loaded: boolean;
  dirty: boolean;
  unsynced: boolean;
  saving: boolean;
  applying: boolean;
  error: unknown;
};
class DraftSession {
  snapshot: Snapshot;
  recovery: object | null = null;
  listeners = new Set<() => void>();
  record: WorkspaceDraft | null = null;
  token = "";
  persisted = "";
  initialJson: string;
  timer: ReturnType<typeof setTimeout> | undefined;
  inFlight: Promise<void> | null = null;
  loading: Promise<void> | null = null;
  constructor(
    readonly path: string,
    initial: object,
  ) {
    try {
      this.recovery = JSON.parse(sessionStorage.getItem(`draft-recovery:${path}`) ?? "null");
    } catch {
      /* A browser can disable session storage. */
    }
    this.initialJson = JSON.stringify(initial);
    this.snapshot = {
      value: initial,
      loaded: false,
      dirty: false,
      unsynced: false,
      saving: false,
      applying: false,
      error: null,
    };
  }
  preserveCopy = () => {
    this.recovery = structuredClone(this.snapshot.value);
    try {
      sessionStorage.setItem(`draft-recovery:${this.path}`, JSON.stringify(this.recovery));
    } catch {
      /* The in-memory copy remains available. */
    }
  };
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };
  getSnapshot = () => this.snapshot;
  publish = (patch: Partial<Snapshot>) => {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const fn of this.listeners) fn();
  };
  load = (): Promise<void> => {
    if (this.loading) return this.loading;
    this.loading = api<DraftRead>(this.path)
      .then((result) => {
        this.token = result.currentToken;
        this.record = result.draft;
        if (result.draft) {
          this.persisted = JSON.stringify(result.draft.content);
          this.publish({ value: result.draft.content, dirty: true });
        }
        this.publish({ loaded: true, error: null });
      })
      .catch((error) => {
        if (errorCodeOf(error) === "DRAFT_CONFLICT") this.preserveCopy();
        this.publish({ error });
        throw error;
      })
      .finally(() => {
        this.loading = null;
      });
    return this.loading;
  };
  set = (value: object) => {
    if (!this.snapshot.loaded || this.snapshot.applying) return;
    this.publish({ value, dirty: true, unsynced: true, error: null });
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.flush().catch(() => {});
    }, 750);
  };
  flush = async (): Promise<void> => {
    clearTimeout(this.timer);
    if (this.inFlight) {
      await this.inFlight;
      if (this.snapshot.unsynced) return this.flush();
      return;
    }
    if (!this.snapshot.unsynced) return;
    this.publish({ saving: true, error: null });
    this.inFlight = (async () => {
      while (this.snapshot.unsynced) {
        const value = this.snapshot.value;
        const encoded = JSON.stringify(value);
        const result = await api<WorkspaceDraft>(this.path, {
          method: "PUT",
          body: {
            revision: this.record?.revision ?? 0,
            baseToken: this.record?.baseToken ?? this.token,
            content: value,
          },
        });
        this.record = result;
        this.persisted = encoded;
        this.publish({ unsynced: JSON.stringify(this.snapshot.value) !== encoded });
      }
    })()
      .catch((error) => {
        if (errorCodeOf(error) === "DRAFT_CONFLICT") this.preserveCopy();
        this.publish({ error });
        throw error;
      })
      .finally(() => {
        this.inFlight = null;
        this.publish({ saving: false });
      });
    return this.inFlight;
  };
}
const sessions = new Set<DraftSession>();
const clientSessions = new WeakMap<object, Map<string, DraftSession>>();
let listening = false;
function sessionFor(client: object, path: string, initial: object) {
  let map = clientSessions.get(client);
  if (!map) {
    map = new Map();
    clientSessions.set(client, map);
  }
  if (!listening && typeof window !== "undefined") {
    window.addEventListener("beforeunload", (event) => {
      if ([...sessions.values()].some((s) => s.snapshot.unsynced)) {
        event.preventDefault();
        event.returnValue = "";
      }
    });
    listening = true;
  }
  let session = map.get(path);
  if (!session) {
    session = new DraftSession(path, initial);
    map.set(path, session);
    sessions.add(session);
  }
  return session;
}
export async function flushWorkspaceDrafts(client: QueryClient, bookId: string) {
  const map = clientSessions.get(client);
  const pending = [...(map?.values() ?? [])].filter(
    (s) => s.path.startsWith(`/books/${bookId}/`) && (s.snapshot.unsynced || s.inFlight),
  );
  await Promise.all(pending.map((s) => s.flush()));
  if (pending.length) await client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
}

/** Sessions outlive screens so a navigation cannot abandon a write or a failed save. */
export function useWorkspaceDraft<T extends object>(
  bookId: string,
  key: string,
  initial: T,
  enabled = true,
) {
  const client = useQueryClient();
  const path = `/books/${bookId}/drafts/${key}`;
  const session = useMemo(() => sessionFor(client, path, initial), [client, path, initial]);
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
  useEffect(() => {
    if (enabled && !session.snapshot.loaded) void session.load().catch(() => {});
    return () => {
      void session.flush().catch(() => {});
    };
  }, [enabled, session]);
  const initialJson = JSON.stringify(initial);
  useEffect(() => {
    if (state.dirty || state.applying) return;
    session.publish({ value: JSON.parse(initialJson) });
    // Parsing can update active metadata after this editor first loads. Refresh
    // the base revision with pristine values; never rebase an existing draft.
    if (enabled && state.loaded && session.initialJson !== initialJson) {
      session.initialJson = initialJson;
      session.publish({ loaded: false });
      void (async () => {
        if (session.loading) await session.loading;
        await session.load();
      })().catch(() => {});
    }
  }, [enabled, initialJson, session, state.applying, state.dirty, state.loaded]);
  useEffect(() => {
    if (!state.dirty || state.saving) return;
    void client.invalidateQueries({ queryKey: queryKeys.book(bookId) });
  }, [bookId, client, state.dirty, state.saving]);
  const setValue = useCallback(
    (next: T | ((old: T) => T)) =>
      session.set(typeof next === "function" ? next(session.snapshot.value as T) : next),
    [session],
  );
  const refresh = useCallback(async () => {
    await client.invalidateQueries({
      predicate: (q) => q.queryKey.includes(bookId) || q.queryKey[0] === queryKeys.books[0],
    });
  }, [bookId, client]);
  const apply = useCallback(async () => {
    session.publish({ applying: true, error: null });
    try {
      await session.flush();
      if (!session.record) {
        session.publish({ unsynced: true });
        await session.flush();
      }
      const book = await api<BookDetail>(`${path}/apply`, {
        method: "POST",
        body: { revision: session.record?.revision },
      });
      session.record = null;
      session.persisted = "";
      session.publish({ dirty: false, unsynced: false, value: initial });
      client.setQueryData(queryKeys.book(bookId), book);
      await refresh();
      await session.load();
      return book;
    } catch (error) {
      if (errorCodeOf(error) === "DRAFT_CONFLICT") session.preserveCopy();
      session.publish({ error });
      throw error;
    } finally {
      session.publish({ applying: false });
    }
  }, [bookId, client, initial, path, refresh, session]);
  const discard = useCallback(async () => {
    session.publish({ applying: true, error: null });
    try {
      clearTimeout(session.timer);
      if (session.inFlight) await session.inFlight.catch(() => {});
      if (session.record)
        await api(path, { method: "DELETE", body: { revision: session.record.revision } });
      session.record = null;
      session.publish({ dirty: false, unsynced: false, value: initial });
      await refresh();
      await session.load();
    } catch (error) {
      session.publish({ error });
    } finally {
      session.publish({ applying: false });
    }
  }, [initial, path, refresh, session]);
  return {
    ...state,
    value: state.value as T,
    setValue,
    flush: session.flush,
    apply,
    discard,
    hasRecovery: session.recovery !== null,
    reload: async () => {
      session.preserveCopy();
      session.publish({ applying: true });
      clearTimeout(session.timer);
      if (session.inFlight) await session.inFlight.catch(() => {});
      session.publish({ value: initial, dirty: false, unsynced: false, loaded: false });
      try {
        await refresh();
        await session.load();
      } finally {
        session.publish({ applying: false });
      }
    },
    download: () => {
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(session.recovery ?? session.snapshot.value, null, 2)], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `${key}-draft.json`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    retry: () => (session.snapshot.loaded ? session.flush() : session.load()),
  };
}
